// src/pages/mlb/MlbBattersProjections.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useLayoutEffect,
  useRef,
} from "react";
/* ======================================================================
   CONFIG
   ====================================================================== */
const SOURCE =
  import.meta.env?.VITE_MLB_BATTERS_URL ||
  "/data/mlb/latest/batter_projections.json";
const SITE_ID_SOURCE =
  import.meta.env?.VITE_MLB_SITE_ID_URL || "/data/mlb/latest/site_ids.json";
const SITES = {
  dk: { key: "dk", label: "DK", logo: "/logos/dk.png" },
  fd: { key: "fd", label: "FD", logo: "/logos/fd.png" },
  both: { key: "both", label: "Both" },
};
/* ======================================================================
   HEADER ALIASES
   ====================================================================== */
const ALIASES = {
  bo: ["bo", "batting order", "order", "spot"],
  hand: ["h", "hand", "handedness", "throws", "bats"],
  dk_pos: ["dk pos", "dk position", "dkpos", "pos", "position"],
  fd_pos: ["fd pos", "fd position", "fdpos"],
  v: ["v", "imp total", "imp. total", "impliedtotal", "imp_total"],
  player: ["player", "name", "batter", "hitter", "player name"],
  dk_sal: ["dk sal", "dk salary", "draftkings salary", "dk$"],
  fd_sal: ["fd sal", "fd salary", "fanduel salary", "fd$"],
  team: ["team", "teamabbrev", "tm"],
  opp: ["opp", "opponent"],
  ab: ["ab", "at bats", "at_bats"],
  hits: ["h__2", "h_2", "hits", "hits_total"],
  one_b: ["1b", "singles", "single"],
  two_b: ["2b", "doubles", "double"],
  three_b: ["3b", "triples", "triple"],
  hr: ["hr", "home runs", "homeruns"],
  rbi: ["rbi", "runs batted in", "runs_batted_in"],
  r: ["r", "runs"],
  sb: ["sb", "stolen bases", "stolen_bases"],
  bb: ["bb", "walks"],
  k: ["k", "so", "strikeouts"],
  dk_proj: ["dk proj", "dk projection", "dk pts", "draftkings proj"],
  dk_val: ["dk val", "dk value"],
  dk_pown: ["dk pown%", "dk pown", "dk own%", "dk ownership%", "dk_pown"],
  fd_proj: ["fd proj", "fd projection", "fd pts", "fanduel proj"],
  fd_val: ["fd val", "fd value"],
  fd_pown: ["fd pown%", "fd pown", "fd own%", "fd ownership%", "fd_pown"],
  dk_floor: ["dk floor", "dk_floor"],
  dk_ceiling: ["dk ceiling", "dk_ceiling"],
  fd_floor: ["fd floor", "fd_floor"],
  fd_ceiling: ["fd ceiling", "fd_ceiling"],
  dk_rtg: ["dk rtg", "dk rating", "dk_rating"],
  fd_rtg: ["fd rtg", "fd rating", "fd_rating"],
};
/* ======================================================================
   HELPERS
   ====================================================================== */
const lc = (s) => String(s ?? "").toLowerCase().trim();
const buildLowerMap = (row) => {
  const low = {};
  for (const [k, v] of Object.entries(row)) low[lc(k)] = v;
  return low;
};
const firstKey = (obj, names) => names.map(lc).find((n) => n in obj);
const pick = (obj, names) => obj[firstKey(obj, names) ?? ""];
const keynorm = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[^a-z0-9 ]/g, "");
const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/[,$%\s]/g, ""));
  return Number.isFinite(n) ? n : null;
};
const pctNum = (v) => {
  const n = num(v);
  if (n === null) return null;
  return Math.max(0, Math.min(100, n));
};
const fmtMoney0 = (v) =>
  num(v) === null ? "" : `$${num(v).toLocaleString()}`;
const fmt1 = (v) => (num(v) === null ? "" : num(v).toFixed(1));
const fmt2 = (v) => (num(v) === null ? "" : num(v).toFixed(2));
const fmtPct1 = (v) =>
  v === null || v === undefined || v === "" ? "" : `${pctNum(v).toFixed(1)}%`;
const teamLogo = (team) =>
  `/logos/mlb/${String(team || "").toUpperCase()}.png`;
/* ======================================================================
   SITE ID / SLATE HELPERS
   ====================================================================== */
function buildSiteIdIndex(raw) {
  const out = {
    dk: new Map(),
    fd: new Map(),
  };
  if (!raw || typeof raw !== "object") return out;
  for (const siteKey of ["dk", "fd"]) {
    const arr = Array.isArray(raw?.[siteKey]) ? raw[siteKey] : [];
    for (const row of arr) {
      const nameKey = keynorm(row?.name);
      const teamKey = String(row?.team || "").toUpperCase().trim();
      const comboKey = `${nameKey}__${teamKey}`;
      out[siteKey].set(comboKey, {
        id: row?.id ?? null,
        salary: num(row?.salary),
        early_id: row?.early_id ?? null,
        early_salary: num(row?.early_salary),
        late_id: row?.late_id ?? null,
        late_salary: num(row?.late_salary),
        pos: row?.pos ?? null,
        pos_primary: row?.pos_primary ?? null,
        team: row?.team ?? null,
        opp: row?.opp ?? null,
        name: row?.name ?? null,
      });
    }
  }
  return out;
}
function getSiteIdEntry(siteIdIndex, row, site) {
  const siteKey = site === "fd" ? "fd" : "dk";
  const nameKey = keynorm(row?.player);
  const teamVal = String(row?.team || "").toUpperCase().trim();
  const comboKey = `${nameKey}__${teamVal}`;
  return siteIdIndex?.[siteKey]?.get(comboKey) ?? null;
}
function getSlateSalary(entry, slate) {
  if (!entry) return null;
  if (slate === "early") return num(entry.early_salary);
  if (slate === "late") return num(entry.late_salary);
  return num(entry.salary);
}
function hasSlateEntry(entry, slate) {
  if (!entry) return false;
  if (slate === "early") {
    return !!entry.early_id && num(entry.early_salary) !== null;
  }
  if (slate === "late") {
    return !!entry.late_id && num(entry.late_salary) !== null;
  }
  if (slate === "main") {
    const sal = num(entry.salary);
    return !!entry.id && sal !== null && sal > 0 && sal < 50000;
  }
  if (slate === "all") {
    return (
      (!!entry.id && num(entry.salary) !== null && num(entry.salary) > 0) ||
      (!!entry.early_id &&
        num(entry.early_salary) !== null &&
        num(entry.early_salary) > 0) ||
      (!!entry.late_id &&
        num(entry.late_salary) !== null &&
        num(entry.late_salary) > 0)
    );
  }
  return true;
}
function rowMatchesSlate(siteIdIndex, row, site, slate) {
  if (slate === "all") return true;
  if (site === "dk" || site === "fd") {
    const entry = getSiteIdEntry(siteIdIndex, row, site);
    return hasSlateEntry(entry, slate);
  }
  const dkEntry = getSiteIdEntry(siteIdIndex, row, "dk");
  const fdEntry = getSiteIdEntry(siteIdIndex, row, "fd");
  return hasSlateEntry(dkEntry, slate) || hasSlateEntry(fdEntry, slate);
}
function getRenderedSalary(siteIdIndex, row, site, slate, baseValue) {
  if (slate === "all") return num(baseValue);
  const entry = getSiteIdEntry(siteIdIndex, row, site);
  return getSlateSalary(entry, slate);
}
/* ======================================================================
   NORMALIZER
   ====================================================================== */
function normalizeRow(row) {
  const low = buildLowerMap(row);
  return {
    bo: num(pick(low, ALIASES.bo)),
    hand: (pick(low, ALIASES.hand) || "").toUpperCase(),
    dk_pos: (pick(low, ALIASES.dk_pos) || "").toUpperCase(),
    fd_pos: (pick(low, ALIASES.fd_pos) || "").toUpperCase(),
    v: num(pick(low, ALIASES.v)),
    player: pick(low, ALIASES.player),
    dk_sal: num(pick(low, ALIASES.dk_sal)),
    fd_sal: num(pick(low, ALIASES.fd_sal)),
    team: (pick(low, ALIASES.team) || "").toUpperCase(),
    opp: (pick(low, ALIASES.opp) || "").toUpperCase(),
    ab: num(pick(low, ALIASES.ab)),
    hits: num(pick(low, ALIASES.hits)),
    one_b: num(pick(low, ALIASES.one_b)),
    two_b: num(pick(low, ALIASES.two_b)),
    three_b: num(pick(low, ALIASES.three_b)),
    hr: num(pick(low, ALIASES.hr)),
    rbi: num(pick(low, ALIASES.rbi)),
    r: num(pick(low, ALIASES.r)),
    sb: num(pick(low, ALIASES.sb)),
    bb: num(pick(low, ALIASES.bb)),
    k: num(pick(low, ALIASES.k)),
    dk_proj: num(pick(low, ALIASES.dk_proj)),
    dk_val: num(pick(low, ALIASES.dk_val)),
    dk_pown: pctNum(pick(low, ALIASES.dk_pown)),
    fd_proj: num(pick(low, ALIASES.fd_proj)),
    fd_val: num(pick(low, ALIASES.fd_val)),
    fd_pown: pctNum(pick(low, ALIASES.fd_pown)),
    dk_floor: num(pick(low, ALIASES.dk_floor)),
    dk_ceiling: num(pick(low, ALIASES.dk_ceiling)),
    fd_floor: num(pick(low, ALIASES.fd_floor)),
    fd_ceiling: num(pick(low, ALIASES.fd_ceiling)),
    dk_rtg: num(pick(low, ALIASES.dk_rtg)),
    fd_rtg: num(pick(low, ALIASES.fd_rtg)),
  };
}
/* ======================================================================
   DATA HOOK
   ====================================================================== */
function useJson(url) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const ct = (r.headers.get("content-type") || "").toLowerCase();
        if (!ct.includes("application/json")) {
          const txt = await r.text();
          throw new Error(
            `Expected JSON, got ${ct || "unknown"}: ${txt.slice(0, 30)}…`
          );
        }
        const j = await r.json();
        const raw = Array.isArray(j)
          ? j
          : j?.rows || j?.data || j?.items || [];
        const data = raw.map(normalizeRow).filter((r) => r.player);
        if (alive) setRows(data);
      } catch (e) {
        if (alive) setErr(String(e.message || e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  return { rows, loading, err };
}
/* ======================================================================
   LAST-UPDATED
   ====================================================================== */
function toMetaUrl(urlLike) {
  return String(urlLike || "").replace(/[^/]+$/, "meta.json");
}
function parseMetaToDate(meta) {
  const iso =
    meta?.updated_iso ||
    meta?.updated_utc ||
    meta?.source_mtime_iso ||
    meta?.last_updated ||
    meta?.timestamp;
  if (iso) {
    const d = new Date(iso);
    if (!isNaN(d)) return d;
  }
  const epoch = meta?.updated_epoch ?? meta?.epoch;
  if (Number.isFinite(epoch)) {
    const d = new Date(epoch * (epoch > 10_000_000_000 ? 1 : 1000));
    if (!isNaN(d)) return d;
  }
  return null;
}
function useLastUpdatedFromSource(sourceUrl) {
  const url = useMemo(() => toMetaUrl(sourceUrl), [sourceUrl]);
  const [date, setDate] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, {
          cache: "no-store",
        });
        if (!r.ok) return;
        const meta = await r.json();
        const d = parseMetaToDate(meta);
        if (alive) setDate(d);
      } catch (_) {}
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  return date
    ? date.toLocaleString(undefined, {
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;
}
/* ======================================================================
   CSV EXPORT
   ====================================================================== */
const escapeCSV = (s) => {
  const v = String(s ?? "");
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
};
const CSV_FMT2_KEYS = new Set([
  "one_b",
  "two_b",
  "three_b",
  "hr",
  "rbi",
  "r",
  "sb",
  "bb",
]);
function downloadCSV(
  rows,
  cols,
  site,
  slate,
  siteIdIndex,
  fname = "mlb_batter_projections.csv"
) {
  const header = cols.map((c) => c.label).join(",");
  const body = rows
    .map((r) =>
      cols
        .map((c) => {
          const k = c.key;
          let val = r[k];
          if (k === "dk_sal") {
            val = getRenderedSalary(siteIdIndex, r, "dk", slate, r.dk_sal);
            val = fmtMoney0(val);
          } else if (k === "fd_sal") {
            val = getRenderedSalary(siteIdIndex, r, "fd", slate, r.fd_sal);
            val = fmtMoney0(val);
          } else if (k === "dk_pown" || k === "fd_pown") {
            val = fmtPct1(val);
          } else if (typeof val === "number") {
            val = CSV_FMT2_KEYS.has(k) ? fmt2(val) : fmt1(val);
          }
          return escapeCSV(val ?? "");
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob([header + "\n" + body], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}
/* ======================================================================
   TEAMS DROPDOWN
   ====================================================================== */
function TeamsDropdown({ allTeams, selected, onChange, isDark }) {
  const allSet = new Set(selected || []);
  const toggle = (tm) => {
    const next = new Set(allSet);
    next.has(tm) ? next.delete(tm) : next.add(tm);
    onChange(Array.from(next));
  };
  const summaryClass =
    "px-3 py-1.5 rounded-lg text-sm font-medium shadow border " +
    (isDark
      ? "bg-slate-950 text-slate-100 border-slate-700"
      : "bg-white text-gray-800 border-gray-200");
  const panelClass =
    "absolute mt-2 left-0 z-20 rounded-xl shadow-lg p-2 w-[260px] max-w-[90vw] border " +
    (isDark
      ? "bg-slate-950 border-slate-700 text-slate-100"
      : "bg-white border-gray-200 text-gray-900");
  const buttonBase =
    "px-2 py-1 rounded-md text-xs border " +
    (isDark
      ? "bg-slate-900 border-slate-700 hover:bg-slate-800"
      : "bg-gray-100 border-gray-200 hover:bg-gray-200");
  return (
    <details className="relative z-[80]">
      <summary className="list-none cursor-pointer">
        <span className={summaryClass}>
          Teams ({selected.length}/{allTeams.length})
        </span>
      </summary>
      <div className={panelClass}>
        <div className="flex items-center justify-between mb-2 text-xs">
          <button
            className={buttonBase}
            onClick={(e) => {
              e.preventDefault();
              onChange([...allTeams]);
            }}
          >
            Select all
          </button>
          <button
            className={buttonBase}
            onClick={(e) => {
              e.preventDefault();
              onChange([]);
            }}
          >
            Clear all
          </button>
        </div>
        <div className="grid grid-cols-4 gap-1 max-h-64 overflow-hidden pr-1">
          {allTeams.map((t) => (
            <label
              key={t}
              className={
                "flex items-center gap-1 text-xs " +
                (isDark ? "text-slate-100" : "text-gray-800")
              }
            >
              <input
                type="checkbox"
                className="accent-indigo-600"
                checked={allSet.has(t)}
                onChange={() => toggle(t)}
              />
              <span>{t}</span>
            </label>
          ))}
        </div>
      </div>
    </details>
  );
}
/* ======================================================================
   CONDITIONAL FORMATTING
   ====================================================================== */
const HIGHER_IS_BETTER = new Set([
  "ab",
  "one_b",
  "two_b",
  "three_b",
  "hr",
  "rbi",
  "r",
  "sb",
  "bb",
  "k",
  "dk_proj",
  "dk_val",
  "fd_proj",
  "fd_val",
  "dk_floor",
  "dk_ceiling",
  "fd_floor",
  "fd_ceiling",
  "dk_rtg",
  "fd_rtg",
  "v",
]);
const LOWER_IS_BETTER = new Set(["dk_pown", "fd_pown"]);
function computeStats(rows, cols) {
  const stats = {};
  for (const c of cols) {
    const key = c.key;
    let min = Infinity;
    let max = -Infinity;
    let any = false;
    for (const r of rows) {
      const v = r[key];
      if (v == null || isNaN(Number(v))) continue;
      const n = Number(v);
      any = true;
      if (n < min) min = n;
      if (n > max) max = n;
    }
    if (any && min !== max) stats[key] = { min, max };
  }
  return stats;
}
function colorFor(value, key, stats, isDark = false) {
  if (value == null || isNaN(Number(value))) return null;
  const st = stats[key];
  if (!st) return null;
  const dir = HIGHER_IS_BETTER.has(key)
    ? "higher"
    : LOWER_IS_BETTER.has(key)
    ? "lower"
    : null;
  if (!dir) return null;
  let t = (Number(value) - st.min) / (st.max - st.min);
  t = Math.max(0, Math.min(1, t));
  if (dir === "lower") t = 1 - t;
  let h, s, l;
  if (isDark) {
    if (t < 0.5) {
      const u = t / 0.5;
      h = 8 + u * 42;
      s = 90;
      l = 70 - u * 8;
    } else {
      const u = (t - 0.5) / 0.5;
      h = 50 + u * 70;
      s = 88;
      l = 62 + u * 8;
    }
  } else {
    if (t < 0.5) {
      const u = t / 0.5;
      h = 8 + u * 42;
      s = 85;
      l = 48 - u * 4;
    } else {
      const u = (t - 0.5) / 0.5;
      h = 50 + u * 70;
      s = 80;
      l = 44 - u * 4;
    }
  }
  return `hsl(${h} ${s}% ${l}%)`;
}
/* ======================================================================
   PAGE
   ====================================================================== */
export default function MlbBattersProjections() {
  const { rows, loading, err } = useJson(SOURCE);
  const updatedText = useLastUpdatedFromSource(SOURCE);
  const [siteIdData, setSiteIdData] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${SITE_ID_SOURCE}?_=${Date.now()}`, {
          cache: "no-store",
        });
        if (!r.ok) return;
        const j = await r.json();
        if (alive) setSiteIdData(j);
      } catch {}
    })();
    return () => {
      alive = false;
    };
  }, []);
  const siteIdIndex = useMemo(() => buildSiteIdIndex(siteIdData), [siteIdData]);
  const [site, setSite] = useState("both");
  const [slate, setSlate] = useState("all");
  const posOptions = ["C", "1B", "2B", "3B", "SS", "OF", "UTIL"];
  const [posSel, setPosSel] = useState([...posOptions]);
  const allTeams = useMemo(
    () =>
      Array.from(
        new Set(
          rows.map((r) => String(r.team || "").toUpperCase()).filter(Boolean)
        )
      ).sort(),
    [rows]
  );
  const [teamsSel, setTeamsSel] = useState(allTeams);
  useEffect(() => {
    if (allTeams.length && (!teamsSel || teamsSel.length === 0))
      setTeamsSel(allTeams);
  }, [allTeams]); // eslint-disable-line react-hooks/exhaustive-deps
  const [q, setQ] = useState("");
  const [theme, setTheme] = useState("dark");
  const isDark = theme === "dark";
  const COLS_ORDER = useMemo(
    () => [
      { key: "bo", label: "BO" },
      { key: "hand", label: "H" },
      { key: "dk_pos", label: "DK Pos", site: "dk" },
      { key: "fd_pos", label: "FD Pos", site: "fd" },
      { key: "v", label: "V" },
      { key: "player", label: "Player", sticky: true },
      { key: "dk_sal", label: "DK Sal", site: "dk" },
      { key: "fd_sal", label: "FD Sal", site: "fd" },
      { key: "team", label: "Team" },
      { key: "opp", label: "Opp" },
      { key: "ab", label: "AB" },
      { key: "hits", label: "H" },
      { key: "one_b", label: "1B" },
      { key: "two_b", label: "2B" },
      { key: "three_b", label: "3B" },
      { key: "hr", label: "HR" },
      { key: "rbi", label: "RBI" },
      { key: "r", label: "R" },
      { key: "sb", label: "SB" },
      { key: "bb", label: "BB" },
      { key: "k", label: "K" },
      { key: "dk_proj", label: "DK Proj", site: "dk" },
      { key: "dk_val", label: "DK Val", site: "dk" },
      { key: "dk_pown", label: "DK pOWN%", site: "dk" },
      { key: "fd_proj", label: "FD Proj", site: "fd" },
      { key: "fd_val", label: "FD Val", site: "fd" },
      { key: "fd_pown", label: "FD pOWN%", site: "fd" },
      { key: "dk_floor", label: "DK Floor", site: "dk" },
      { key: "dk_ceiling", label: "DK Ceiling", site: "dk" },
      { key: "fd_floor", label: "FD Floor", site: "fd" },
      { key: "fd_ceiling", label: "FD Ceiling", site: "fd" },
      { key: "dk_rtg", label: "DK Rtg", site: "dk" },
      { key: "fd_rtg", label: "FD Rtg", site: "fd" },
    ],
    []
  );
  const columns = useMemo(() => {
    return COLS_ORDER.filter((c) => {
      if (site !== "both" && c.site && c.site !== site) return false;
      return rows.some((r) => r[c.key] !== null && r[c.key] !== undefined);
    });
  }, [COLS_ORDER, rows, site]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const posSet = new Set(posSel);
    const teamSet = new Set(teamsSel);
    return rows.filter((r) => {
      if (!rowMatchesSlate(siteIdIndex, r, site, slate)) return false;
      if (posSet.size) {
        const dkPosStr = String(r.dk_pos || "").toUpperCase();
        const fdPosStr = String(r.fd_pos || "").toUpperCase();
        const pickPosStr =
          site === "dk"
            ? dkPosStr
            : site === "fd"
            ? fdPosStr
            : `${dkPosStr}/${fdPosStr}`;
        const rowPositions = pickPosStr
          .split(/[\/,\s]+/)
          .map((p) => p.trim())
          .filter(Boolean);
        if (rowPositions.length && !rowPositions.some((p) => posSet.has(p))) {
          return false;
        }
      }
      if (teamSet.size && !teamSet.has(String(r.team || "").toUpperCase()))
        return false;
      if (!needle) return true;
      return `${r.player ?? ""} ${r.team ?? ""} ${r.opp ?? ""}`
        .toLowerCase()
        .includes(needle);
    });
  }, [rows, q, posSel, teamsSel, site, slate, siteIdIndex]);
  const [sort, setSort] = useState({ key: "dk_proj", dir: "desc" });
  const sorted = useMemo(() => {
    const { key, dir } = sort;
    const sgn = dir === "asc" ? 1 : -1;
    const arr = [...filtered];
    arr.sort((a, b) => {
      if (key === "dk_sal") {
        const av = getRenderedSalary(siteIdIndex, a, "dk", slate, a.dk_sal);
        const bv = getRenderedSalary(siteIdIndex, b, "dk", slate, b.dk_sal);
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return (av - bv) * sgn;
      }
      if (key === "fd_sal") {
        const av = getRenderedSalary(siteIdIndex, a, "fd", slate, a.fd_sal);
        const bv = getRenderedSalary(siteIdIndex, b, "fd", slate, b.fd_sal);
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return (av - bv) * sgn;
      }
      const av = num(a[key]);
      const bv = num(b[key]);
      if (av === null && bv === null)
        return String(a[key] ?? "").localeCompare(String(b[key] ?? "")) * sgn;
      if (av === null) return 1;
      if (bv === null) return -1;
      return (av - bv) * sgn;
    });
    return arr;
  }, [filtered, sort, siteIdIndex, slate]);
  const onSort = (col) => {
    setSort((prev) =>
      prev.key !== col.key
        ? { key: col.key, dir: "desc" }
        : { key: col.key, dir: prev.dir === "desc" ? "asc" : "desc" }
    );
  };
  const playerThRef = useRef(null);
  const [playerColWidth, setPlayerColWidth] = useState(0);
  useLayoutEffect(() => {
    const calc = () => {
      if (playerThRef.current)
        setPlayerColWidth(playerThRef.current.getBoundingClientRect().width);
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [columns, sorted]);
  const stats = useMemo(() => computeStats(sorted, columns), [sorted, columns]);
  const TABLE_FMT2_KEYS = useMemo(
    () =>
      new Set([
        "one_b",
        "two_b",
        "three_b",
        "hr",
        "rbi",
        "r",
        "sb",
        "bb",
      ]),
    []
  );
  const textSz = "text-[12px]";
  const headerCellBase =
    "px-2 py-1 font-semibold text-center whitespace-nowrap cursor-pointer select-none";
  const cellClsBase = "px-2 py-0 text-center";
  const renderCell = (key, r) => {
    switch (key) {
      case "bo":
        return r.bo ?? "";
      case "dk_pown":
      case "fd_pown":
        return fmtPct1(r[key]);
      case "dk_sal":
        return fmtMoney0(getRenderedSalary(siteIdIndex, r, "dk", slate, r.dk_sal));
      case "fd_sal":
        return fmtMoney0(getRenderedSalary(siteIdIndex, r, "fd", slate, r.fd_sal));
      case "hand":
      case "dk_pos":
      case "fd_pos":
      case "team":
      case "opp":
        return r[key] || "";
      case "player":
        return r.player || "";
      default: {
        const v = r[key];
        if (typeof v === "number") return TABLE_FMT2_KEYS.has(key) ? fmt2(v) : fmt1(v);
        return v ?? "";
      }
    }
  };
  const outerClass = isDark
    ? "min-h-screen bg-slate-950 text-slate-100"
    : "min-h-screen bg-slate-100 text-slate-900";
  const cardClass = isDark
    ? "rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm"
    : "rounded-2xl border border-slate-200 bg-white shadow-sm";
  const headerBgClass = isDark
    ? "bg-slate-900 text-slate-200"
    : "bg-gray-50 text-gray-800";
  return (
    <div className={outerClass}>
      <div className="w-full px-4 md:px-6 py-5">
        <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-xl font-bold tracking-tight">MLB — Batter Projections</h1>
            <span className={"text-xs font-normal italic " + (isDark ? "text-slate-100" : "text-slate-700")}>Updated: {updatedText || "—"}</span>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setSite("both");
                setSlate("all");
                setPosSel([...posOptions]);
                setTeamsSel(allTeams);
                setQ("");
                setTheme("dark");
                setSort({ key: "dk_proj", dir: "desc" });
              }}
              className={
                "h-8 rounded-md border px-3 text-xs " +
                (isDark
                  ? "bg-slate-950/80 border-slate-700 text-slate-100"
                  : "bg-white border-gray-300 text-gray-900")
              }
            >
              Reset
            </button>
            <div
              className={
                "inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " +
                (isDark
                  ? "bg-slate-900/90 ring-slate-700/80"
                  : "bg-white ring-slate-300")
              }
            >
              <button
                type="button"
                onClick={() => setTheme("dark")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (isDark
                    ? "bg-sky-500 text-slate-950 shadow-sm"
                    : "text-slate-500 hover:text-slate-800")
                }
              >
                Dark
              </button>
              <button
                type="button"
                onClick={() => setTheme("light")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (!isDark
                    ? "bg-sky-500 text-slate-950 shadow-sm"
                    : "text-slate-400 hover:text-slate-100")
                }
              >
                Light
              </button>
            </div>
          </div>
        </header>
        <section aria-label="Site and slate" className={cardClass + " mb-4"}>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3 px-4 py-3">
            <div
              className={
                "inline-flex flex-wrap items-center gap-1 border-b " +
                (isDark
                  ? "border-slate-800"
                  : "border-slate-200")
              }
            >
              {["dk", "fd", "both"].map((k) => (
                <button
                  key={k}
                  onClick={() => setSite(k)}
                  className={`h-9 px-3 border-b-2 text-xs font-semibold inline-flex items-center gap-1.5 transition ${
                    site === k
                      ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
                      : isDark
                      ? "border-transparent text-slate-400 hover:text-white"
                      : "border-transparent text-slate-500 hover:text-slate-900"
                  }`}
                  title={`Show ${SITES[k].label}`}
                >
                  {k !== "both" ? (
                    <img
                      src={SITES[k].logo}
                      alt={SITES[k].label}
                      className="w-4 h-4"
                    />
                  ) : null}
                  <span>{SITES[k].label}</span>
                </button>
              ))}
            </div>
            <div
              className={
                "inline-flex flex-wrap items-center gap-1 border-b " +
                (isDark
                  ? "border-slate-800"
                  : "border-slate-200")
              }
              title="Slate filter"
            >
              {[
                { k: "all", label: "All" },
                { k: "early", label: "Early" },
                { k: "main", label: "Main" },
                { k: "late", label: "Late" },
              ].map((x) => (
                <button
                  key={x.k}
                  onClick={() => setSlate(x.k)}
                  className={`h-9 px-3 border-b-2 text-xs font-semibold transition ${
                    slate === x.k
                      ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
                      : isDark
                      ? "border-transparent text-slate-400 hover:text-white"
                      : "border-transparent text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {x.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {err && (
          <div
            className={
              "mb-2 font-semibold " +
              (isDark ? "text-red-400" : "text-red-600")
            }
          >
            Data load error: {err}
          </div>
        )}
        <section aria-label="Player projections" className={cardClass}>
          <div className={"flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 " + (isDark ? "border-slate-800" : "border-slate-200")}>
            <h2 className="text-sm font-semibold">Batter Projections</h2>
            <button
              className={
                "h-8 px-3 rounded-md text-xs font-semibold transition " +
                (isDark
                  ? "bg-sky-500 text-slate-950 hover:bg-sky-400 shadow-sm"
                  : "bg-blue-600 text-white hover:bg-blue-700 shadow-sm")
              }
              onClick={() =>
                downloadCSV(sorted, columns, site, slate, siteIdIndex)
              }
            >
              Export CSV
            </button>
          </div>
          <div className="px-4 pt-2">
            <div className="flex flex-wrap items-center gap-1 border-b border-slate-700/30">
              {posOptions.map((p) => {
                const active = posSel.includes(p);
                return (
                  <button
                    key={p}
                    onClick={() =>
                      setPosSel((prev) =>
                        prev.includes(p)
                          ? prev.filter((x) => x !== p)
                          : [...prev, p]
                      )
                    }
                    className={`h-9 px-2.5 border-b-2 text-xs font-semibold transition ${
                      active
                        ? isDark
                          ? "border-sky-400 text-white"
                          : "border-sky-500 text-slate-900"
                        : isDark
                        ? "border-transparent text-slate-400 hover:text-white"
                        : "border-transparent text-slate-500 hover:text-slate-900"
                    }`}
                    title="Toggle position"
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="ml-1">
              <TeamsDropdown
                allTeams={allTeams}
                selected={teamsSel}
                onChange={setTeamsSel}
                isDark={isDark}
              />
            </div>
            <input
              className={
                "h-8 w-full sm:ml-auto sm:w-64 rounded-md border px-3 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 " +
                (isDark
                  ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500"
                  : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400")
              }
              placeholder="Search batter / team / opp…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="overflow-x-auto">
            <div className="max-h-[70vh] overflow-y-auto">
              <table
                className={`w-full border-separate ${textSz}`}
                style={{ borderSpacing: 0 }}
              >
                <thead className={headerBgClass + " sticky top-0 z-40"}>
                  <tr>
                    {columns.map((c) => (
                      <th
                        key={c.key}
                        ref={c.sticky ? playerThRef : undefined}
                        className={`${headerCellBase} ${
                          c.sticky ? "sticky left-0 z-50" : ""
                        }`}
                        style={
                          c.sticky
                            ? {
                                left: 0,
                                minWidth: playerColWidth || 160,
                                backgroundColor: isDark ? "#0f172a" : "#f9fafb",
                              }
                            : undefined
                        }
                        onClick={() => onSort(c)}
                        title="Click to sort"
                      >
                        <div className="inline-flex items-center gap-1">
                          <span>{c.label}</span>
                          <span className={isDark ? "text-slate-400" : "text-gray-400"}>
                            {sort.key === c.key
                              ? sort.dir === "desc"
                                ? "▼"
                                : "▲"
                              : "▲"}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td
                        className={
                          cellClsBase +
                          " text-center " +
                          (isDark ? "text-slate-400" : "text-gray-500")
                        }
                        colSpan={columns.length}
                      >
                        Loading…
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    sorted.map((r, i) => {
                      const rowBg =
                        i % 2 === 0
                          ? isDark
                            ? "bg-slate-950"
                            : "bg-white"
                          : isDark
                          ? "bg-slate-900"
                          : "bg-gray-50";
                      const cellCls =
                        cellClsBase +
                        " " +
                        (isDark ? "text-slate-100" : "text-gray-900");
                      return (
                        <tr
                          key={`${r.player}-${i}`}
                          className={
                            rowBg +
                            " " +
                            (isDark
                              ? "hover:bg-slate-900/80 transition-colors"
                              : "hover:bg-blue-50 transition-colors")
                          }
                        >
                          {columns.map((c) => {
                            let value = r[c.key];
                            if (c.key === "dk_sal") {
                              value = getRenderedSalary(
                                siteIdIndex,
                                r,
                                "dk",
                                slate,
                                r.dk_sal
                              );
                            }
                            if (c.key === "fd_sal") {
                              value = getRenderedSalary(
                                siteIdIndex,
                                r,
                                "fd",
                                slate,
                                r.fd_sal
                              );
                            }
                            const textColor = colorFor(value, c.key, stats, isDark);
                            const stickyCls = c.sticky
                              ? `px-2 py-1 text-left sticky left-0 z-[5] ${rowBg} shadow-[inset_-6px_0_6px_-6px_rgba(0,0,0,0.35)] ${
                                  isDark ? "text-slate-100" : "text-gray-900"
                                }`
                              : cellCls;
                            return (
                              <td
                                key={c.key}
                                className={stickyCls}
                                title={String(value ?? "")}
                              >
                                {c.key === "player" ? (
                                  <div className="flex items-center gap-2">
                                    <img
                                      src={teamLogo(r.team)}
                                      alt=""
                                      className="w-4 h-4 rounded-sm object-contain"
                                      onError={(e) =>
                                        (e.currentTarget.style.visibility =
                                          "hidden")
                                      }
                                    />
                                    <span className="whitespace-nowrap">
                                      {renderCell(c.key, r)}
                                    </span>
                                  </div>
                                ) : (
                                  <span
                                    style={textColor ? { color: textColor, fontWeight: 600 } : undefined}
                                  >
                                    {renderCell(c.key, r)}
                                  </span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  {!loading && !sorted.length && (
                    <tr>
                      <td
                        className={
                          cellClsBase +
                          " text-center " +
                          (isDark ? "text-slate-500" : "text-gray-500")
                        }
                        colSpan={columns.length}
                      >
                        No data.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
        <div
          className={
            "mt-2 text-[12px] flex flex-wrap gap-3 " +
            (isDark ? "text-slate-500" : "text-gray-500")
          }
        >
          <span>pOWN% = Projected Ownership</span>
          <span>Slate filter works on DK, FD, and Both</span>
        </div>
      </div>
    </div>
  );
}